<template>
    <div class="cryptoWIthdrawContainer">
        <div v-if="!userStore.withdrawPassword">
            <div class="textTitle"> {{ $t('profile.withdrawalPassword') }}</div>
            
            <div class="crypto_field">
                <div class="label_margin">{{ $t('withdraw.securityPassword') }}</div>
                <div class="pin-input-row">
                    <input
                        v-for="(item, index) in newPasswordArray"
                        :key="'new-' + index"
                        ref="newInputsRef"
                        v-model="newPasswordArray[index]"
                        type="password"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        maxlength="1"
                        class="pin-box"
                        @input="handlePinInput($event, index, 'new')"
                        @keydown.delete="handlePinBackspace($event, index, 'new')"
                    />
                </div>
                <div v-if="passwordValidationNotice" class="errorNotice">
                    {{ $t('loginRegister.passwordForamt') }}
                </div>
            </div>

            <div class="crypto_field">
                <div class="label_margin">{{ $t('profile.cnofirmNewPassword') }}</div>
                <div class="pin-input-row">
                    <input
                        v-for="(item, index) in confirmPasswordArray"
                        :key="'confirm-' + index"
                        ref="confirmInputsRef"
                        v-model="confirmPasswordArray[index]"
                        type="password"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        maxlength="1"
                        class="pin-box"
                        @input="handlePinInput($event, index, 'confirm')"
                        @keydown.delete="handlePinBackspace($event, index, 'confirm')"
                    />
                </div>
                <div v-if="confirmPassStr && newPassStr && confirmPassStr !== newPassStr" class="errorNotice">
                    {{ $t('loginRegister.differentPassword') }}
                </div>
            </div>

            <div class="btn_container">
                <MainButton 
                    :title="$t('profile.save')" 
                    :activeStatus="isSubmitBtnActivated"
                    @click="handleUpdatePassword" 
                    :radius="'10px'"
                />
            </div>
        </div>

        <div v-else>
            <div class="crypto_field">
                <div>{{ $t('withdraw.address') }}</div>
                <van-field v-model="address" :placeholder="$t('withdraw.address')">
                    <template #right-icon>
                        <div class="right_Icons">
                            <van-icon name="user-circle-o" color="#FFF" size="22" style="padding-right: 15px;"
                                @click="showAddressPopup" />
                            <van-icon name="scan" size="22" color="#FFF" @click="handleStartScan" />
                        </div>
                    </template>
                </van-field>
            </div>
            <div class="crypto_field">
                <div>{{ $t('withdraw.network') }}</div>
                <div class="networkField" @click="showNetworkPopup">{{ network ? network : $t('withdraw.network') }}</div>
            </div>
            <div class="crypto_field">
                <div>{{ $t('withdraw.securityPassword') }}</div>
                <van-field v-model="securityPassword" type="password"
                    :placeholder="$t('withdraw.securityPassword')"></van-field>
            </div>
            <div class="crypto_field">
                <div>{{ $t('withdraw.withdrawalAmount') }}</div>
                <van-field v-model="withdrawalAmount" type="number"
                    :placeholder="$t('withdraw.withdrawalMinAmount', { value: withdrawMin })">
                    <template #right-icon>
                        <div class="rightText"> USDT <span>Max</span></div>
                    </template>
                </van-field>
            </div>

            <div class="btn_container">
                <MainButton :title="$t('common.sake')" :activeStatus="buttonActive" @click="confirmWithdraw" :radius="'10px'"/>
            </div>
        </div>

        <van-popup v-model:show="showScanner" class="transparent-scanner-popup" @closed="stopScanner"
            :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }">
            <div class="scanner-wrapper">
                <div id="reader"></div>
            </div>
        </van-popup>
        <AddressBookPopup ref="addressBookPopupRef" />
        <NetworkSelectPopup ref="networkPopupRef" :networkList="networkList" @selectNetwork="setSelecteddNetwork" />
        <LoadingScreen ref="loadingRef" />
        <StatusPopup ref="statusRef" :is-show-success="isSuccess" :content="toastContent" />
    </div>
</template>

<script setup lang="ts">
import { formatCurrency } from '@/utils/commonFunction';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import MainButton from '../components/common/mainButton.vue';
import AddressBookPopup from './components/addressBookPopup.vue';
import NetworkSelectPopup from './components/networkSelectPopup.vue';
import { callNetworkOptions, callWithdraw, useScanner } from '@/view/withdraw/supportScript';
import { useUserStore } from '@/stores/user';
import LoadingScreen from '@/components/common/loadingScreen.vue';
import StatusPopup from '@/components/common/statusPopup.vue';
import { checkConfirmPasswordValidation, checkWithdrawPasswordValidation, updateWithdrawPassword } from '@/view/profile/supportScript';

const userStore = useUserStore();
const address = ref('');
const network = ref('');
const securityPassword = ref('');
const withdrawalAmount = ref('');
const withdrawMin = ref(0);
const isSuccess = ref(false);
const statusRef = ref();
const loadingRef = ref();
const toastContent = ref('');
const addressBookPopupRef = ref();
const networkPopupRef = ref();
const networkList = ref([]);
const selectedNetwork = ref<any>({});
const uId = ref(userStore.id);

const newPasswordArray = ref(['', '', '', '', '', '']);
const confirmPasswordArray = ref(['', '', '', '', '', '']);
const newInputsRef = ref<HTMLInputElement[]>([]);
const confirmInputsRef = ref<HTMLInputElement[]>([]);

const newPassStr = computed(() => newPasswordArray.value.join(''));
const confirmPassStr = computed(() => confirmPasswordArray.value.join(''));

const passwordValidationNotice = ref(false);
const confirmPasswordValidationNotice = ref(false);

const isSubmitBtnActivated = computed(() => {
    return (
        newPassStr.value.length === 6 &&
        confirmPassStr.value.length === 6 &&
        !passwordValidationNotice.value &&
        !confirmPasswordValidationNotice.value &&
        newPassStr.value === confirmPassStr.value
    );
});

const handlePinInput = (e: any, index: number, type: 'new' | 'confirm') => {
    const val = e.target.value;
    const inputs = type === 'new' ? newInputsRef.value : confirmInputsRef.value;
    const dataArr = type === 'new' ? newPasswordArray.value : confirmPasswordArray.value;

    if (val.length > 1) {
        dataArr[index] = val.slice(-1);
    }

    if (val && index < 5) {
        inputs[index + 1].focus();
    }
};

const handlePinBackspace = (e: KeyboardEvent, index: number, type: 'new' | 'confirm') => {
    const inputs = type === 'new' ? newInputsRef.value : confirmInputsRef.value;
    const dataArr = type === 'new' ? newPasswordArray.value : confirmPasswordArray.value;

    if (e.key === 'Backspace' && !dataArr[index] && index > 0) {
        inputs[index - 1].focus();
    }
};

const handleUpdatePassword = () => {
    updateWithdrawPassword('', newPassStr.value, ref(false), toastContent, isSuccess, statusRef);
};

// --- EXISTING WITHDRAWAL LOGIC ---
const buttonActive = computed(() => {
    return (
        address.value.trim() !== '' &&
        network.value.trim() !== '' &&
        securityPassword.value.trim() !== '' &&
        withdrawalAmount.value !== '' &&
        Number(withdrawalAmount.value) > 0
    );
});

const { showScanner, startScanner, stopScanner } = useScanner();

const handleStartScan = () => {
    startScanner('reader', (result) => {
        address.value = result;
    });
};

const showAddressPopup = () => addressBookPopupRef.value.openPopup();
const showNetworkPopup = () => networkPopupRef.value.openPopup();

const showError = (message: string) => {
    toastContent.value = message;
    isSuccess.value = false;
    statusRef.value.open();
};
const showSuccess = (message: string) => {
    toastContent.value = message;
    isSuccess.value = true;
    statusRef.value.open();
};

const confirmWithdraw = async () => {
    loadingRef.value.open();
    const { data } = await callWithdraw(address.value, uId.value, withdrawalAmount.value, selectedNetwork.value.id, securityPassword.value);
    loadingRef.value.close();
    if (data.code == 200) {
        showSuccess(data.msg);
    } else {
        showError(data);
    }
};

const getNetworkOption = async (addr?: string) => {
    if (addr) address.value = addr;
    const { network_list } = await callNetworkOptions(address.value);
    networkList.value = network_list;
};

const setSelecteddNetwork = (networkData: any) => {
    selectedNetwork.value = networkData;
    network.value = networkData?.channelName;
    networkPopupRef.value.closePopup();
};

watch(() => address.value, () => {
    getNetworkOption();
    network.value = '';
});

// Watch the generated strings to trigger validation notices
watch(newPassStr, (val) => {
    checkWithdrawPasswordValidation(val, passwordValidationNotice);
});

watch(confirmPassStr, (val) => {
    checkConfirmPasswordValidation(val, ref(confirmPassStr.value), ref(newPassStr.value), confirmPasswordValidationNotice);
});

onUnmounted(() => stopScanner());
onMounted(() => getNetworkOption());
</script>

<style scoped>
.cryptoWIthdrawContainer { padding: 25px; height: 100% }
.crypto_field { color: #FFF; font-weight: bold; font-size: 17px; margin-bottom: 25px; }
.textTitle { padding: 0px 10px 25px; font-size: 18px; text-align: center; font-weight: 600; color: #FFF; }
.label_margin { margin-bottom: 12px; font-size: 15px; }
.errorNotice { color: #E00000; font-size: 14px; padding: 5px 0 0 0; font-weight: 400; }

.pin-input-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 10px;
}

.pin-box {
    width: 100%;
    aspect-ratio: 1 / 1;
    max-width: 48px;
    background: #101629;
    border: 1px solid transparent;
    border-radius: 8px;
    text-align: center;
    font-size: 20px;
    color: #fff;
    outline: none;
    transition: all 0.2s;
}

.pin-box:focus {
    border: 1px solid #35a5f8;
    background: #1c2a47;
}

.pin-box::-webkit-outer-spin-button,
.pin-box::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.networkField { background: #101629 !important; border-radius: 0.5rem; margin-top: 5px; padding: 14px 15px; color: #ffffff40; font-weight: 400; height: 25px; display: flex; align-items: center; }
.van-cell { background: #101629 !important; border-radius: 0.5rem; margin-top: 5px; font-size: 15px; }
.right_Icons { display: flex; }
.rightText { color: #35a5f8; font-weight: bold; }
.rightText span { color: #fff; font-weight: 400; padding-left: 5px; }
.btn_container { padding: 30px 10%; }
.crypto_field :deep(.van-field__control) { color: #fff !important; font-weight: 400 !important; height: 33px; }
.transparent-scanner-popup { background: transparent !important; overflow: visible; width: 80%; }
.scanner-wrapper { position: relative; display: flex; flex-direction: column; align-items: center; }
#reader { width: 100%; border-radius: 12px; overflow: hidden; background: #000; }
:deep(#reader video) { width: 100% !important; height: auto !important; object-fit: cover; }

@media (max-width: 360px) {
  .cryptoWIthdrawContainer {
    padding: 15px;
  }

  .crypto_field {
    font-size: 14px;
    margin-bottom: 18px;
  }

  .textTitle {
    font-size: 16px;
    padding-bottom: 15px;
  }

  .label_margin {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .errorNotice {
    font-size: 12px;
  }

  .btn_container {
    padding: 20px 10px;
  }
}
</style>